import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Field';
import { courses } from '../../data/academics';
import { listings } from '../../data/housing';
import { events, organizations } from '../../data/campus';
import { opportunities } from '../../data/career';
import { groups, people } from '../../data/community';
import { programmes, universityProfiles } from '../../data/admissions';
import { knowledgeResources, serviceProviders } from '../../data/marketplace';
import { academicServices } from '../../data/services';
import { challenges, clubs, communities, posts } from '../../data/social';
import { POST_KIND_LABEL } from '../../utils/social';
interface Hit {
  id: string;
  group: string;
  label: string;
  meta: string;
  to: string;
}
function buildIndex(): Hit[] {
  return [...courses.map(course => ({
    id: `c-${course.id}`,
    group: 'Courses',
    label: `${course.code} — ${course.title}`,
    meta: `${course.units} units · ${course.lecturer}`,
    to: '/academics'
  })), ...listings.map(listing => ({
    id: `l-${listing.id}`,
    group: 'Housing',
    label: listing.name,
    meta: `${listing.neighbourhood} · ${listing.minutesToCampus} min to campus`,
    to: `/housing/${listing.id}`
  })), ...events.map(event => ({
    id: `e-${event.id}`,
    group: 'Events',
    label: event.title,
    meta: `${event.dateLabel} · ${event.venue}`,
    to: '/community'
  })), ...organizations.map(org => ({
    id: `o-${org.id}`,
    group: 'Organizations',
    label: org.name,
    meta: `${org.members} members`,
    to: '/community'
  })), ...people.map(person => ({
    id: `p-${person.id}`,
    group: 'People',
    label: `${person.displayName} · @${person.username}`,
    meta: person.headline,
    to: `/community/people/${person.username}`
  })), ...groups.map(group => ({
    id: `g-${group.id}`,
    group: 'Groups',
    label: group.name,
    meta: `${group.members} members · ${group.privacy}`,
    to: '/community'
  })), {
    id: 'network-programme',
    group: 'Campus network',
    label: 'Become a Campuzon Campus Representative',
    meta: 'Verified field work, referrals and campus intelligence',
    to: '/network'
  }, ...academicServices.map(service => ({
    id: `as-${service.id}`,
    group: 'Academic services',
    label: service.name,
    meta: `${service.institutionId.toUpperCase()} · ${service.category}`,
    to: '/services'
  })), ...universityProfiles.map(university => ({
    id: `u-${university.id}`,
    group: 'Universities',
    label: university.name,
    meta: `${university.city}, ${university.country}`,
    to: '/discover'
  })), ...programmes.map(programme => ({
    id: `pr-${programme.id}`,
    group: 'Programmes',
    label: programme.title,
    meta: `${programme.level} · ${programme.deadlineLabel}`,
    to: '/discover'
  })), ...knowledgeResources.map(resource => ({
    id: `k-${resource.id}`,
    group: 'Knowledge',
    label: resource.title,
    meta: `${resource.kind} · ${resource.author}`,
    to: '/marketplace'
  })), ...serviceProviders.map(provider => ({
    id: `sp-${provider.id}`,
    group: 'Providers',
    label: `${provider.displayName} · @${provider.handle}`,
    meta: provider.headline,
    to: '/marketplace'
  })), ...opportunities.map(opportunity => ({
    id: `j-${opportunity.id}`,
    group: 'Opportunities',
    label: opportunity.title,
    meta: `${opportunity.employer} · ${opportunity.kind}`,
    to: '/opportunities'
  })), ...communities.map(community => ({
    id: `cm-${community.id}`,
    group: 'Communities',
    label: community.name,
    meta: `${community.category} · ${community.members.toLocaleString()} members`,
    to: '/social'
  })), ...clubs.map(club => ({
    id: `cl-${club.id}`,
    group: 'Clubs',
    label: club.name,
    meta: `${club.category} · ${club.verified ? 'Campuzon Verified' : 'Not verified'}`,
    to: '/social'
  })), ...challenges.map(challenge => ({
    id: `ch-${challenge.id}`,
    group: 'Challenges',
    label: challenge.name,
    meta: `${challenge.category} · ${challenge.organiser}`,
    to: '/social'
  })),
  // Only content posted to a campus or institution audience is indexed.
  // Community, group and connection-scoped posts stay out of search entirely
  // rather than relying on a filter at display time.
  ...posts.filter(post => post.state === 'published' && (post.audience === 'campus' || post.audience === 'institution')).map(post => ({
    id: `po-${post.id}`,
    group: post.kind === 'question' ? 'Questions' : 'Posts',
    label: post.body.length > 78 ? `${post.body.slice(0, 78)}…` : post.body,
    meta: `${POST_KIND_LABEL[post.kind]} · ${post.author.name}`,
    to: '/social'
  }))];
}
export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const index = useMemo(buildIndex, []);

  /* ⌘K / Ctrl+K opens search from anywhere, as the trigger advertises. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const hits = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (trimmed.length < 2) return [];
    return index.filter(hit => hit.label.toLowerCase().includes(trimmed) || hit.meta.toLowerCase().includes(trimmed)).slice(0, 8);
  }, [index, query]);
  return <>
      <button type="button" onClick={() => setOpen(true)} aria-label="Search Campuzon" className="flex min-h-touch w-11 items-center justify-center gap-2.5 rounded-xl border border-line bg-canvas px-3 text-body-sm text-ink-subtle transition-colors duration-150 ease-out hover:border-line-strong hover:text-ink-muted md:w-full md:max-w-sm md:justify-start" data-id="element-4658" data-mp="1h9hiuyyqb5a4">
        <SearchIcon aria-hidden className="h-5 w-5 shrink-0" data-id="element-4659" data-mp="ygnhj7u13bod" />
        <span className="hidden md:inline" data-id="element-4660" data-mp="tic3sm0m4h0v">Search Campuzon…</span>
        <kbd className="ml-auto hidden shrink-0 rounded border border-line bg-surface px-1.5 py-0.5 text-caption font-medium text-ink-subtle md:inline" data-id="element-4661" data-mp="tkojesiwefar">
          ⌘K
        </kbd>
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Search Campuzon" description="Universities, programmes, courses, housing, people, groups, communities, clubs, challenges, campus posts, questions, knowledge, providers, events and opportunities. Posts inside a community, group or private circle are never indexed here." data-id="element-4662" data-mp="2nvtswdgo1ly0">
        <Input autoFocus value={query} onChange={event => setQuery(event.target.value)} placeholder="Try “ensuite”, “control systems” or “internship”" aria-label="Search query" data-id="element-4663" data-mp="fed4l1aymwwc" />
        <div className="mt-4" data-id="element-4664" data-mp="3iw536yjlqf3i">
          {query.trim().length < 2 ? <p className="py-6 text-center text-[13px] text-ink-muted" data-id="element-4665" data-mp="3ntezmy5err0u">
              Type at least two characters to search.
            </p> : hits.length === 0 ? <p className="py-6 text-center text-[13px] text-ink-muted" data-id="element-4666" data-mp="p8kfekluill">
              Nothing matched “{query.trim()}”.
            </p> : <ul className="divide-y divide-line" data-id="element-4667" data-mp="176lwrszqp334">
              {hits.map(hit => <li key={hit.id} data-id="element-4668" data-mp="1c5fdrve8jc81">
                  <button type="button" onClick={() => {
              setOpen(false);
              setQuery('');
              navigate(hit.to);
            }} className="flex w-full items-center justify-between gap-4 px-1 py-2.5 text-left transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-4669" data-mp="nq92p1azcbjf">
                    <span className="min-w-0" data-id="element-4670" data-mp="16gh2pajdulve">
                      <span className="block truncate text-[13px] font-medium text-ink" data-id="element-4671" data-mp="16jczuwnep6is">
                        {hit.label}
                      </span>
                      <span className="block truncate text-[12px] text-ink-muted" data-id="element-4672" data-mp="1fi878c26smno">{hit.meta}</span>
                    </span>
                    <span className="shrink-0 text-[11px] font-medium uppercase tracking-wide text-ink-subtle" data-id="element-4673" data-mp="1bgv07yncydf6">
                      {hit.group}
                    </span>
                  </button>
                </li>)}
            </ul>}
        </div>
      </Modal>
    </>;
}